import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ArrowRight } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const easePremium = [0.22, 1, 0.36, 1];

  // ⚠️ IMPORTANTE: Reemplaza '51999999999' con tu número de WhatsApp real 
  // (incluye el código de país, sin el signo '+' ni espacios).
  const phoneNumber = "51955000321"; 
  const message = "Deseo cotizar software o servicio, vengo de la pagina web";
  const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { to: '/', label: 'Inicio' },
    { to: '/nosotros', label: 'Nosotros' },
    { to: '/servicios', label: 'Servicios' },
    { to: '/blog', label: 'Blog' },
    { to: '/galeria', label: 'Galería' },
    { to: '/contacto', label: 'Contacto' }
  ];

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Bloquear scroll del body cuando el menú móvil está abierto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: easePremium }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled 
            ? 'bg-white/95 backdrop-blur-md border-b border-slate-200/60 shadow-sm' 
            : 'bg-white/80 backdrop-blur-sm'
        }`}
      >
        <div className="container mx-auto px-4 md:px-6 lg:px-12">
          <div className="flex items-center justify-between h-16 md:h-20">
            
            {/* ========================================== */}
            {/* LOGO                                        */}
            {/* ========================================== */}
            <Link to="/" className="flex items-center gap-2.5 group">
              <img 
                src="/images/logo22.png" 
                alt="Visioner7" 
                className="h-9 md:h-10 w-auto"
              />
              <span className="text-xl md:text-2xl font-bold text-slate-900 tracking-tight group-hover:text-emerald-600 transition-colors duration-300">
                Visioner7
              </span>
            </Link>

            {/* ========================================== */}
            {/* NAV DESKTOP                                 */}
            {/* ========================================== */}
            <nav className="hidden lg:flex items-center gap-1">
              {navItems.map((item) => {
                const isActive = location.pathname === item.to;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={`relative px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                      isActive 
                        ? 'text-emerald-600' 
                        : 'text-slate-700 hover:text-slate-900'
                    }`}
                  >
                    {item.label}
                    {/* Indicador de página activa */}
                    {isActive && (
                      <motion.div
                        layoutId="activeNavIndicator"
                        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-emerald-600"
                        transition={{ type: "spring", stiffness: 300, damping: 30 }}
                      />
                    )}
                  </Link>
                );
              })}
            </nav>

            {/* ========================================== */}
            {/* CTA DESKTOP + MENÚ MÓVIL                    */}
            {/* ========================================== */}
            <div className="flex items-center gap-3">
              {/* Botón CTA - Solo desktop (Ahora abre WhatsApp) */}
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="hidden lg:inline-flex items-center gap-2 bg-slate-900 text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-300 hover:bg-emerald-600 hover:shadow-lg hover:shadow-emerald-600/20"
              >
                Solicitar cotización
                <ArrowRight className="w-4 h-4" />
              </a>

              {/* Botón menú móvil */}
              <motion.button
                className="lg:hidden p-2 rounded-lg text-slate-700 hover:bg-slate-100 transition-colors"
                onClick={() => setIsOpen(!isOpen)}
                whileTap={{ scale: 0.95 }}
                aria-label="Menú"
              >
                {isOpen ? <X size={22} /> : <Menu size={22} />}
              </motion.button>
            </div>
          </div>
        </div>
      </motion.header>

      {/* ========================================== */}
      {/* MENÚ MÓVIL (Panel deslizante premium)       */}
      {/* ========================================== */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Overlay oscuro */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-40 lg:hidden"
              onClick={() => setIsOpen(false)}
            />

            {/* Panel del menú */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed top-0 right-0 bottom-0 w-full max-w-sm bg-white z-50 lg:hidden shadow-2xl"
            >
              {/* Header del menú móvil */}
              <div className="flex items-center justify-between p-6 border-b border-slate-200">
                <Link to="/" className="flex items-center gap-2.5" onClick={() => setIsOpen(false)}>
                  <img 
                    src="/images/logo22.png" 
                    alt="Visioner7" 
                    className="h-8 w-auto"
                  />
                  <span className="text-xl font-bold text-slate-900 tracking-tight">
                    Visioner7
                  </span>
                </Link>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors"
                  aria-label="Cerrar menú"
                >
                  <X size={22} />
                </button>
              </div>

              {/* Navegación móvil */}
              <nav className="flex flex-col p-6 gap-1">
                {navItems.map((item, index) => {
                  const isActive = location.pathname === item.to;
                  return (
                    <motion.div
                      key={item.to}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05, duration: 0.3 }}
                    >
                      <Link
                        to={item.to}
                        onClick={() => setIsOpen(false)}
                        className={`block px-4 py-3 rounded-lg text-base font-medium transition-all duration-200 ${
                          isActive 
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' 
                            : 'text-slate-700 hover:bg-slate-50 hover:text-slate-900'
                        }`}
                      >
                        {item.label}
                      </Link>
                    </motion.div>
                  );
                })}
              </nav>

              {/* CTA en menú móvil (Ahora abre WhatsApp) */}
              <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-slate-200 bg-white">
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsOpen(false)}
                  className="flex items-center justify-center gap-2 bg-slate-900 text-white px-6 py-3.5 rounded-lg font-semibold transition-all hover:bg-emerald-600 w-full"
                >
                  Solicitar cotización
                  <ArrowRight className="w-4 h-4" />
                </a>
                <p className="text-xs text-slate-500 text-center mt-3">
                  Respuesta en menos de 24 horas
                </p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;